'use client'

// About — Document 03. The HUMAN page. Cinematic founder story in
// four chapters: Hero → A. Origin → B. Mission & Vision → C. Team
// Portraits → D. Roadmap. Background warms then cools, mirroring
// the emotional arc. Content lives in the `about` translation
// namespace; both /about and /sobre-nosotros render this same
// component via cookie-driven next-intl locale.

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

// ─── Tokens ──────────────────────────────────────────────────────────────
const EARTH_DEEP   = '#1F1608'
const EARTH_WARM_1 = '#2E2010'
const EARTH_WARM_2 = '#3D2A14'
const EARTH_FWD    = '#2E1F0A'
const CREAM        = '#F5ECD8'
const CREAM_80     = 'rgba(245,236,216,0.80)'
const CREAM_60     = 'rgba(245,236,216,0.60)'
const CREAM_45     = 'rgba(245,236,216,0.45)'
const CREAM_18     = 'rgba(245,236,216,0.18)'
const CREAM_10     = 'rgba(245,236,216,0.10)'
const OLIVE        = '#5B7005'
const LIME         = '#9FAF08'
const FONT_BODY    = 'var(--font-body)'
const FONT_DISP    = 'var(--font-display)'
const FONT_SERIF   = 'var(--font-serif)'

// ─── Image paths ─────────────────────────────────────────────────────────
const PHOTOS = {
  heroCeremony: '/ABOUT/Personales/54579287501_350a7de9f4_k.jpg',
  hugo:         '/ABOUT/Personales/hugo-portrait-v2.jpg',
  clara:        '/ABOUT/Personales/clara-portrait-v2.jpg',
  alex:         '/ABOUT/Personales/alejandro-portrait-v2.jpg',
  pitching1:    '/ABOUT/Personales/BORT3083.jpg',
  pitching2:    '/ABOUT/Personales/BORT3092.jpg',
  claraStage:   '/ABOUT/Personales/1752166541958.jpg',
  workspace:    '/ABOUT/Personales/1752166542022.jpg',
}

// ─── Section header (massive letter + chrome + title + subtitle) ────────
function SectionHeader({
  letter, chrome, title, subtitle,
}: { letter: 'A' | 'B' | 'C' | 'D'; chrome: string; title: string; subtitle: string }) {
  const letterRef   = useRef<HTMLDivElement>(null)
  const titleRef    = useRef<HTMLHeadingElement>(null)
  const subRef      = useRef<HTMLParagraphElement>(null)
  const chromeRef   = useRef<HTMLDivElement>(null)
  const reduced     = useReducedMotion()

  useEffect(() => {
    if (reduced) return
    gsapSetup()
    const trig = ScrollTrigger.create({
      trigger: letterRef.current,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        const tl = gsap.timeline()
        if (letterRef.current) {
          tl.fromTo(letterRef.current,
            { autoAlpha: 0, scale: 0.6, rotation: -6 },
            { autoAlpha: 1, scale: 1, rotation: 0, duration: 1.2, ease: 'power3.out' })
        }
        if (chromeRef.current) {
          tl.fromTo(chromeRef.current, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.5 }, '-=0.7')
        }
        if (titleRef.current) {
          const words = titleRef.current.querySelectorAll<HTMLElement>('[data-word]')
          tl.fromTo(words, { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.06 }, '-=0.4')
        }
        if (subRef.current) {
          tl.fromTo(subRef.current, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.5 }, '-=0.3')
        }
      },
    })
    return () => trig.kill()
  }, [reduced])

  return (
    <div style={{ position: 'relative', padding: 'clamp(24px, 4vh, 56px) 0 clamp(48px, 8vh, 96px)' }}>
      <div
        ref={letterRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -40,
          left: 'clamp(-30px, -2vw, 0px)',
          fontFamily: FONT_SERIF,
          fontStyle: 'italic',
          fontWeight: 400,
          fontSize: 'clamp(15rem, 25vw, 30rem)',
          lineHeight: 0.78,
          letterSpacing: '-0.05em',
          color: OLIVE,
          opacity: 0.16,
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 0,
          willChange: 'transform, opacity',
        }}
      >
        {letter}
      </div>
      <div style={{ position: 'relative', zIndex: 1, paddingLeft: 'clamp(80px, 14vw, 220px)' }}>
        <div
          ref={chromeRef}
          style={{
            fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.38em',
            textTransform: 'uppercase', fontWeight: 600, color: OLIVE,
            marginBottom: 22,
          }}
        >
          {chrome}
        </div>
        <h2
          ref={titleRef}
          style={{
            fontFamily: FONT_DISP,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 500,
            letterSpacing: '-0.025em',
            lineHeight: 1.0,
            color: CREAM,
            margin: 0,
          }}
        >
          {title.split(' ').map((w, i) => (
            <span key={i} data-word style={{ display: 'inline-block', marginRight: '0.25em' }}>{w}</span>
          ))}
        </h2>
        <p
          ref={subRef}
          style={{
            fontFamily: FONT_SERIF, fontStyle: 'italic',
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            color: LIME, margin: '14px 0 0', lineHeight: 1.2,
            letterSpacing: '-0.01em', fontWeight: 400,
          }}
        >
          {subtitle}
        </p>
      </div>
    </div>
  )
}

// ─── Mini chapter nav (fixed top, appears after hero) ──────────────────
function MiniNav({ items, activeId }: { items: { id: string; letter: string; label: string }[]; activeId: string }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById('about-hero')
      if (!hero) return
      const r = hero.getBoundingClientRect()
      setVisible(r.bottom < 80)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const jump = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  return (
    <nav
      aria-label="About chapters"
      className="about-mini-nav"
      data-visible={visible || undefined}
    >
      {items.map(it => (
        <button
          key={it.id}
          type="button"
          className={`about-mini-nav-pill ${activeId === it.id ? 'active' : ''}`}
          onClick={() => jump(it.id)}
          aria-current={activeId === it.id ? 'true' : undefined}
        >
          <span className="about-mini-nav-letter">{it.letter}</span>
          <span className="about-mini-nav-label">{it.label}</span>
        </button>
      ))}
    </nav>
  )
}

// ─── Portrait (Section C — alternating sides) ──────────────────────────
interface PortraitProps {
  side: 'left' | 'right'
  label: string
  firstName: string
  lastName: string
  role: string
  body: string
  tags: string[]
  photoSrc: string
  photoAlt: string
}
function Portrait({ side, label, firstName, lastName, role, body, tags, photoSrc, photoAlt }: PortraitProps) {
  const wrapRef   = useRef<HTMLDivElement>(null)
  const photoRef  = useRef<HTMLDivElement>(null)
  const nameRef   = useRef<HTMLHeadingElement>(null)
  const reduced   = useReducedMotion()

  useEffect(() => {
    if (reduced || !wrapRef.current) return
    gsapSetup()
    const items = wrapRef.current.querySelectorAll<HTMLElement>('[data-portrait-anim]')
    gsap.set(items, { autoAlpha: 0, y: 24 })
    if (photoRef.current) gsap.set(photoRef.current, { autoAlpha: 0, scale: 0.95 })

    const trig = ScrollTrigger.create({
      trigger: wrapRef.current,
      start: 'top 75%',
      once: true,
      onEnter: () => {
        if (photoRef.current) {
          gsap.to(photoRef.current, { autoAlpha: 1, scale: 1, duration: 1.2, ease: 'power3.out' })
        }
        gsap.to(items, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.08, delay: 0.2 })
        // Name char stagger (split into chars manually)
        if (nameRef.current) {
          const chars = nameRef.current.querySelectorAll<HTMLElement>('[data-char]')
          gsap.fromTo(chars,
            { autoAlpha: 0, y: 28 },
            { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.025, delay: 0.35 })
        }
      },
    })

    // Slow Ken Burns — runs continuously while in/near viewport
    let kb: gsap.core.Tween | null = null
    if (photoRef.current) {
      const img = photoRef.current.querySelector('img')
      if (img) {
        kb = gsap.fromTo(img,
          { scale: 1.0 },
          { scale: 1.06, duration: 9, ease: 'sine.inOut', repeat: -1, yoyo: true })
      }
    }
    return () => { trig.kill(); kb?.kill() }
  }, [reduced])

  const isRight = side === 'right'
  return (
    <div
      ref={wrapRef}
      className="about-portrait"
      style={{ direction: isRight ? 'rtl' : 'ltr' }}
    >
      <div
        ref={photoRef}
        className="about-portrait-photo"
        style={{ direction: 'ltr' }}
      >
        <Image
          src={photoSrc}
          alt={photoAlt}
          fill
          sizes="(max-width: 1023px) 100vw, 50vw"
          style={{ objectFit: 'cover' }}
          priority={false}
        />
        <span aria-hidden="true" className="about-portrait-overlay" />
      </div>
      <div className="about-portrait-text" style={{ direction: 'ltr' }}>
        <div data-portrait-anim className="about-portrait-label">{label}</div>
        <h3 ref={nameRef} className="about-portrait-name">
          {Array.from(firstName).map((c, i) => (
            <span key={i} data-char style={{ display: 'inline-block' }}>{c === ' ' ? ' ' : c}</span>
          ))}
        </h3>
        <div data-portrait-anim className="about-portrait-surname">{lastName}</div>
        <div data-portrait-anim className="about-portrait-role">{role}</div>
        <p data-portrait-anim className="about-portrait-body">{body}</p>
        <div data-portrait-anim className="about-portrait-tags">
          {tags.map((t, i) => <span key={i} className="about-tag">{t}</span>)}
        </div>
      </div>
    </div>
  )
}

// ─── Roadmap milestone ─────────────────────────────────────────────────
type MilestoneStatus = 'done' | 'progress' | 'upcoming' | 'future' | 'aspirational'
interface MilestoneProps {
  side: 'left' | 'right'
  date: string
  status: MilestoneStatus
  statusLabel: string
  head: string
  body: string
  highlight?: string
  index: number
}
function Milestone({ side, date, status, statusLabel, head, body, highlight, index }: MilestoneProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  const [pinned, setPinned] = useState(false)

  useEffect(() => {
    if (reduced || !wrapRef.current) return
    gsapSetup()
    const node = wrapRef.current
    gsap.set(node, { autoAlpha: 0, y: 28 })
    const trig = ScrollTrigger.create({
      trigger: node,
      start: 'top 82%',
      once: true,
      onEnter: () => {
        gsap.to(node, {
          autoAlpha: 1, y: 0,
          duration: status === 'aspirational' ? 1.1 : 0.7,
          ease: 'power3.out',
        })
      },
    })
    return () => trig.kill()
  }, [reduced, status])

  return (
    <div
      ref={wrapRef}
      className={`about-milestone about-milestone-${side} about-milestone-${status} ${pinned ? 'pinned' : ''}`}
      data-index={index}
    >
      <div
        className="about-milestone-card"
        onClick={() => setPinned(p => !p)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPinned(p => !p) } }}
        aria-expanded={pinned}
      >
        <div className="about-milestone-status">
          <span className={`about-milestone-dot ${status}`} aria-hidden="true">
            {status === 'done' && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5L4.2 7.2L8 3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="about-milestone-statuslbl">{statusLabel}</span>
        </div>
        <div className="about-milestone-date">{date}</div>
        <h3 className="about-milestone-head">{head}</h3>
        <p className="about-milestone-body">{body}</p>
        {highlight && (
          <div className="about-milestone-highlight">
            <span aria-hidden="true">◎</span>
            <span>{highlight}</span>
          </div>
        )}
      </div>
      <span className="about-milestone-branch" aria-hidden="true" />
      <span className="about-milestone-node" aria-hidden="true" />
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────

// Data the server wrapper feeds us. Each entry already has `photoSrc`
// resolved (CMS URL or fallback). Pass an empty array to fall back to the
// hardcoded 3-team-member / 9-milestone layout.
export type TeamMember = Omit<PortraitProps, 'side'> & { id?: string | number }
export type Milestone = Omit<MilestoneProps, 'side' | 'index'> & { id?: string | number }

interface AboutPageClientProps {
  teamMembers?: TeamMember[]
  milestones?: Milestone[]
}

export default function AboutPageClient({ teamMembers, milestones }: AboutPageClientProps = {}) {
  const t = useTranslations('about')
  const locale = useLocale()
  const contactHref = locale === 'es' ? '/contacto' : '/contact'
  const [activeChapter, setActiveChapter] = useState<string>('about-a')
  const reduced = useReducedMotion()

  // Hero entrance
  const heroRef          = useRef<HTMLElement>(null)
  const heroPhotoRef     = useRef<HTMLDivElement>(null)
  const heroHeadlineRef  = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (reduced || !heroRef.current) return
    gsapSetup()
    const items = heroRef.current.querySelectorAll<HTMLElement>('[data-hero-anim]')
    gsap.set(items, { autoAlpha: 0, y: 22 })
    const tl = gsap.timeline({ delay: 0.15 })
    if (heroHeadlineRef.current) {
      const words = heroHeadlineRef.current.querySelectorAll<HTMLElement>('[data-hero-word]')
      gsap.set(words, { autoAlpha: 0, y: 36 })
      tl.to(words, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.07 })
    }
    if (heroPhotoRef.current) {
      gsap.set(heroPhotoRef.current, { autoAlpha: 0, scale: 0.95 })
      tl.to(heroPhotoRef.current, { autoAlpha: 1, scale: 1, duration: 1.3, ease: 'power3.out' }, 0)
    }
    tl.to(items, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 }, '-=0.6')
  }, [reduced])

  // Chapter tracker for mini-nav
  useEffect(() => {
    const ids = ['about-a', 'about-b', 'about-c', 'about-d']
    const onScroll = () => {
      const vh = window.innerHeight
      let current = ids[0]
      ids.forEach(id => {
        const el = document.getElementById(id)
        if (!el) return
        const r = el.getBoundingClientRect()
        if (r.top <= vh * 0.4 && r.bottom > vh * 0.4) current = id
      })
      setActiveChapter(current)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Roadmap timeline draw + year markers
  const timelineRef = useRef<HTMLDivElement>(null)
  const timelineLineRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (reduced || !timelineRef.current || !timelineLineRef.current) return
    gsapSetup()
    const fill = timelineLineRef.current
    gsap.set(fill, { scaleY: 0, transformOrigin: 'top' })
    const trig = ScrollTrigger.create({
      trigger: timelineRef.current,
      start: 'top 70%',
      end: 'bottom 80%',
      scrub: 0.6,
      onUpdate: (self) => { gsap.to(fill, { scaleY: self.progress, duration: 0.1, overwrite: 'auto' }) },
    })
    return () => trig.kill()
  }, [reduced])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  const yearMarkers = t('yearMarkers').split(',')

  return (
    <main
      style={{
        background: EARTH_DEEP,
        color: CREAM,
        fontFamily: FONT_BODY,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        .about-content { max-width: 1280px; margin: 0 auto; padding: 0 clamp(24px, 5vw, 80px); position: relative; }
        .about-grain {
          position: absolute; inset: 0;
          background-image: url(/grain.svg); background-size: 220px 220px;
          opacity: 0.06; mix-blend-mode: soft-light;
          pointer-events: none; z-index: 1;
        }

        /* ── Hero ─────────────────────────────────────────────────── */
        .about-hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
          gap: clamp(48px, 6vw, 96px);
          align-items: center;
          width: 100%;
        }
        @media (max-width: 1023px) {
          .about-hero-grid { grid-template-columns: 1fr; gap: 32px; }
        }
        .about-hero-photo {
          position: relative;
          width: 100%;
          align-self: stretch;
          min-height: clamp(380px, 58vh, 580px);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 26px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04);
          will-change: transform;
        }
        @media (max-width: 1023px) {
          .about-hero-photo {
            aspect-ratio: 4 / 3;
            min-height: 0;
          }
        }
        @media (min-width: 1024px) {
          .about-hero-photo {
            margin-right: clamp(-80px, -4vw, -20px);
          }
        }
        .about-hero-photo img {
          filter: sepia(0.06) brightness(1.04) saturate(0.95);
          transition: transform 1100ms cubic-bezier(0.22,1,0.36,1);
        }
        .about-hero-photo:hover img { transform: scale(1.03); }
        .about-hero-photo-cap {
          position: absolute; left: 16px; bottom: 14px;
          padding: 7px 14px; border-radius: 999px;
          background: rgba(31,22,8,0.55);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(245,236,216,0.15);
          font-family: ${FONT_BODY}; font-size: 10px;
          letter-spacing: 0.32em; text-transform: uppercase;
          color: ${CREAM_80}; font-weight: 600;
          opacity: 0;
          transform: translateY(6px);
          transition: opacity 320ms ease, transform 320ms ease;
        }
        .about-hero-photo:hover .about-hero-photo-cap {
          opacity: 1; transform: translateY(0);
        }

        .about-hero-headline {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(3rem, 6vw, 5.5rem);
          line-height: 0.98;
          letter-spacing: -0.03em;
          color: ${CREAM};
          margin: 0;
          max-width: 13ch;
        }
        .about-hero-headline em {
          font-family: ${FONT_SERIF}; font-style: italic; font-weight: 400;
          color: ${LIME};
          letter-spacing: -0.01em;
        }
        .about-hero-sub {
          font-family: ${FONT_BODY}; font-size: clamp(15px, 1.2vw, 18px);
          line-height: 1.65; color: ${CREAM_80};
          margin: 28px 0 36px; max-width: 520px;
        }
        .about-hero-jumps { display: flex; flex-wrap: nowrap; gap: 10px; }
        .about-hero-jump {
          display: inline-flex; align-items: center; gap: 8px;
          white-space: nowrap;
          padding: 10px 18px;
          border: 1px solid ${CREAM_18};
          border-radius: 999px;
          background: rgba(255,255,255,0.03);
          color: ${CREAM_80}; font-family: ${FONT_BODY}; font-size: 12px;
          letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600;
          cursor: pointer;
          transition: border-color 280ms ease, background 280ms ease, color 280ms ease, transform 280ms ease;
        }
        .about-hero-jump:hover {
          border-color: ${LIME}; color: ${CREAM}; transform: translateY(-2px);
          background: rgba(159,175,8,0.08);
        }

        .about-hero-bottom {
          position: absolute;
          left: 50%;
          bottom: clamp(20px, 2.4vh, 32px);
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          z-index: 4;
        }
        .about-hero-bottom-cue,
        .about-hero-bottom-page {
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          color: ${CREAM_45}; font-weight: 600;
        }
        .about-hero-cue {
          display: inline-flex; flex-direction: column; gap: 10px;
          align-items: center;
          text-align: center;
        }
        .about-hero-cue > span:first-child {
          padding-left: 0.36em;
        }
        .about-hero-cue-line {
          display: block; width: 1px; height: 36px;
          align-self: center;
          background: linear-gradient(to bottom, ${CREAM_45}, transparent);
          animation: about-hero-cue-pulse 2.4s ease-in-out infinite;
        }
        @keyframes about-hero-cue-pulse {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }

        /* ── Mini-nav ─────────────────────────────────────────────── */
        .about-mini-nav {
          position: fixed; top: 76px; left: 50%;
          transform: translateX(-50%) translateY(-12px);
          display: none;
          gap: 6px;
          padding: 6px;
          background: rgba(31,22,8,0.55);
          border: 1px solid ${CREAM_18};
          border-radius: 999px;
          backdrop-filter: blur(14px) saturate(140%);
          -webkit-backdrop-filter: blur(14px) saturate(140%);
          box-shadow: 0 12px 36px rgba(0,0,0,0.35);
          z-index: 50;
          opacity: 0;
          pointer-events: none;
          transition: opacity 320ms ease, transform 320ms cubic-bezier(0.22,1,0.36,1);
        }
        .about-mini-nav[data-visible] {
          opacity: 1;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0);
        }
        @media (min-width: 768px) {
          .about-mini-nav { display: inline-flex; }
        }
        .about-mini-nav-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 14px;
          border-radius: 999px;
          background: transparent; border: 0; cursor: pointer;
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.22em; text-transform: uppercase; font-weight: 600;
          color: ${CREAM_60};
          transition: background 240ms ease, color 240ms ease;
        }
        .about-mini-nav-pill:hover { color: ${CREAM}; }
        .about-mini-nav-pill.active {
          background: rgba(159,175,8,0.18);
          color: ${LIME};
        }
        .about-mini-nav-letter {
          font-family: ${FONT_SERIF}; font-style: italic; font-weight: 400;
          font-size: 14px; letter-spacing: -0.02em; line-height: 1;
        }

        /* ── Pull quote (Section A) ──────────────────────────────── */
        .about-pullquote {
          font-family: ${FONT_DISP};
          font-size: clamp(1.5rem, 2.4vw, 2rem);
          line-height: 1.4;
          color: ${CREAM};
          font-weight: 400;
          letter-spacing: -0.015em;
          max-width: 820px;
          margin: 0 auto;
          text-align: left;
        }
        .about-pullquote em {
          font-family: ${FONT_SERIF}; font-style: italic;
          color: ${LIME}; font-weight: 400;
        }
        .about-pullquote-body {
          font-family: ${FONT_BODY}; font-size: clamp(15px, 1.1vw, 17px);
          line-height: 1.75; color: ${CREAM_80};
          max-width: 720px; margin: 32px auto 0; text-align: left;
        }

        /* ── Section A milestone split ───────────────────────────── */
        .about-a-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(40px, 6vw, 80px);
          margin-top: clamp(64px, 10vh, 112px);
          align-items: center;
        }
        @media (max-width: 1023px) {
          .about-a-split { grid-template-columns: 1fr; gap: 32px; }
        }
        .about-a-strip {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(14px, 1.6vw, 22px);
          margin-top: clamp(64px, 10vh, 112px);
        }
        @media (max-width: 767px) {
          .about-a-strip { grid-template-columns: 1fr; }
        }
        .about-photo {
          position: relative; aspect-ratio: 4 / 3;
          border-radius: 10px; overflow: hidden;
          box-shadow: 0 18px 50px rgba(0,0,0,0.45);
          transition: transform 320ms cubic-bezier(0.22,1,0.36,1), box-shadow 320ms ease;
        }
        .about-photo img { transition: transform 600ms ease; }
        .about-photo:hover {
          transform: translateY(-3px);
          box-shadow: 0 22px 60px rgba(0,0,0,0.55);
        }
        .about-photo:hover img { transform: scale(1.04); }
        .about-photo-cap {
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.32em; text-transform: uppercase;
          color: ${CREAM_60}; font-weight: 600;
          margin-top: 14px;
        }

        .about-tag {
          display: inline-block;
          padding: 6px 12px;
          border: 1px solid ${CREAM_18};
          border-radius: 999px;
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.28em; text-transform: uppercase;
          color: ${CREAM_60}; font-weight: 600;
          margin: 6px 6px 0 0;
        }

        /* ── Section B mission/vision + SDGs ─────────────────────── */
        .about-statement { margin: clamp(72px, 12vh, 140px) auto 0; max-width: 1000px; }
        .about-statement-label {
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.42em; text-transform: uppercase;
          font-weight: 700; color: ${LIME};
          margin-bottom: 22px;
        }
        .about-statement-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(2rem, 5vw, 4rem);
          line-height: 1.02; letter-spacing: -0.025em;
          color: ${CREAM}; margin: 0;
        }
        .about-statement-h span { display: block; }
        .about-statement-sub {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.25rem, 2vw, 1.75rem);
          line-height: 1.4; color: ${CREAM_80};
          margin: 28px 0 0; font-weight: 400; max-width: 60ch;
        }
        .about-statement-sub em { color: ${LIME}; font-style: italic; }

        .about-sdg-section { margin-top: clamp(88px, 14vh, 160px); }
        .about-sdg-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.5rem, 2.4vw, 2rem);
          color: ${CREAM}; margin: 0 0 clamp(36px, 5vh, 56px);
          letter-spacing: -0.02em; line-height: 1.2;
          max-width: 28ch;
        }
        .about-sdg-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(16px, 2vw, 28px);
        }
        @media (max-width: 1023px) {
          .about-sdg-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 639px) {
          .about-sdg-grid { grid-template-columns: 1fr; }
        }
        .about-sdg-card {
          padding: clamp(28px, 3vw, 40px);
          border: 1px solid ${CREAM_10};
          border-radius: 16px;
          background: rgba(255,255,255,0.025);
          transition: border-color 320ms ease, background 320ms ease,
                      transform 320ms cubic-bezier(0.22,1,0.36,1),
                      box-shadow 320ms ease;
          position: relative; overflow: hidden;
        }
        .about-sdg-card::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(ellipse 80% 40% at 50% 0%, rgba(159,175,8,0.10), transparent 70%);
          opacity: 0; transition: opacity 380ms ease;
          pointer-events: none;
        }
        .about-sdg-card:hover {
          border-color: ${LIME};
          background: rgba(159,175,8,0.04);
          transform: translateY(-4px);
          box-shadow: 0 16px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(159,175,8,0.20);
        }
        .about-sdg-card:hover::before { opacity: 1; }
        .about-sdg-num {
          font-family: ${FONT_SERIF}; font-style: italic; font-weight: 400;
          font-size: clamp(3.5rem, 6vw, 5rem);
          color: ${LIME}; line-height: 0.9; letter-spacing: -0.04em;
          margin: 0 0 14px;
          animation: about-sdg-pulse 4s ease-in-out infinite;
        }
        @keyframes about-sdg-pulse {
          0%, 100% { opacity: 0.92; }
          50%      { opacity: 1;    text-shadow: 0 0 14px rgba(159,175,8,0.35); }
        }
        .about-sdg-title {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.15rem, 1.4vw, 1.4rem);
          color: ${CREAM}; margin: 0 0 12px;
          letter-spacing: -0.015em; line-height: 1.2;
        }
        .about-sdg-body {
          font-family: ${FONT_BODY}; font-size: 14px;
          line-height: 1.65; color: ${CREAM_60}; margin: 0;
        }

        /* ── Section C portraits ─────────────────────────────────── */
        .about-c-tagline {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.2rem, 2vw, 1.6rem);
          color: ${CREAM_80}; max-width: 50ch;
          margin: 0 0 clamp(64px, 10vh, 112px) clamp(80px, 14vw, 220px);
          line-height: 1.4;
        }
        .about-portrait {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          min-height: clamp(560px, 80vh, 760px);
          margin: clamp(56px, 8vh, 96px) 0;
          align-items: center;
        }
        @media (max-width: 1023px) {
          .about-portrait { grid-template-columns: 1fr; gap: 32px; min-height: auto; direction: ltr !important; }
        }
        .about-portrait-photo {
          position: relative;
          aspect-ratio: 3 / 4;
          width: 100%;
          height: 100%;
          min-height: clamp(440px, 64vh, 640px);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 30px 90px rgba(0,0,0,0.55);
        }
        .about-portrait-photo img {
          will-change: transform;
          filter: contrast(1.04) saturate(1.02);
        }
        .about-portrait-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, transparent 60%, rgba(31,22,8,0.45) 100%);
          pointer-events: none;
        }
        .about-portrait-text {
          padding: clamp(24px, 4vw, 64px);
          max-width: 540px;
        }
        .about-portrait-label {
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.42em; text-transform: uppercase;
          font-weight: 600; color: ${LIME};
          margin-bottom: 22px;
        }
        .about-portrait-name {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(3rem, 6vw, 5rem);
          line-height: 0.95; letter-spacing: -0.035em;
          color: ${CREAM}; margin: 0;
        }
        .about-portrait-surname {
          font-family: ${FONT_BODY};
          font-size: clamp(15px, 1.2vw, 19px);
          color: ${CREAM_60};
          letter-spacing: 0.04em;
          margin-top: 12px;
          font-weight: 400;
        }
        .about-portrait-role {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.15rem, 1.8vw, 1.7rem);
          color: ${LIME};
          margin: 22px 0 28px;
          letter-spacing: -0.01em;
          line-height: 1.2;
        }
        .about-portrait-body {
          font-family: ${FONT_BODY};
          font-size: clamp(14px, 1.05vw, 16px);
          line-height: 1.7;
          color: ${CREAM_80};
          margin: 0 0 24px;
          max-width: 46ch;
        }
        .about-portrait-tags { margin-top: 10px; }

        .about-c-closing {
          margin-top: clamp(72px, 12vh, 140px);
          padding-top: clamp(40px, 6vh, 64px);
          border-top: 1px solid ${CREAM_10};
          text-align: center;
        }
        .about-c-closing-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.75rem, 3vw, 2.5rem);
          color: ${CREAM}; margin: 0;
          letter-spacing: -0.02em; line-height: 1.15;
        }
        .about-c-closing-h em {
          font-family: ${FONT_SERIF}; font-style: italic;
          color: ${LIME}; font-weight: 400;
        }
        .about-c-closing-body {
          font-family: ${FONT_BODY}; font-size: clamp(14px, 1.1vw, 17px);
          line-height: 1.7; color: ${CREAM_60};
          max-width: 60ch; margin: 22px auto 0;
        }

        /* ── Section D timeline ──────────────────────────────────── */
        .about-d-intro {
          font-family: ${FONT_BODY};
          font-size: clamp(15px, 1.15vw, 18px);
          line-height: 1.7;
          color: ${CREAM_80};
          max-width: 56ch;
          margin: 0 0 clamp(56px, 8vh, 96px) clamp(80px, 14vw, 220px);
        }
        .about-timeline {
          position: relative;
          padding: clamp(40px, 6vh, 80px) 0;
        }
        @media (min-width: 768px) {
          .about-timeline {
            /* Reserve a gutter on each side so year markers (left) sit
               outside the card grid and don't print under the cards.
               Symmetric padding keeps the axis line at visual center. */
            padding-left:  clamp(120px, 14vw, 180px);
            padding-right: clamp(120px, 14vw, 180px);
          }
        }
        .about-timeline-axis {
          position: absolute;
          top: 0; bottom: 0; left: 50%;
          width: 1px;
          background: ${CREAM_10};
          transform: translateX(-50%);
        }
        .about-timeline-fill {
          position: absolute;
          top: 0; bottom: 0; left: 50%;
          width: 2px;
          background: linear-gradient(to bottom, ${LIME} 0%, ${OLIVE} 100%);
          box-shadow: 0 0 12px rgba(159,175,8,0.55);
          transform: translateX(-50%) scaleY(0);
          transform-origin: top;
        }
        @media (max-width: 767px) {
          .about-timeline-axis,
          .about-timeline-fill { left: 18px; }
        }

        .about-timeline-years {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: clamp(90px, 11vw, 150px);
          pointer-events: none;
        }
        @media (max-width: 767px) {
          .about-timeline-years { display: none; }
        }
        .about-timeline-year {
          position: absolute;
          left: 0;
          font-family: ${FONT_SERIF}; font-style: italic; font-weight: 400;
          font-size: clamp(1.6rem, 2.2vw, 2.4rem);
          color: ${CREAM_45};
          letter-spacing: -0.02em;
        }

        .about-milestone {
          position: relative;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(32px, 5vw, 80px);
          margin: clamp(28px, 4vh, 48px) 0;
          align-items: center;
        }
        .about-milestone-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid ${CREAM_10};
          border-radius: 16px;
          padding: clamp(20px, 2.4vw, 28px);
          cursor: pointer;
          transition: transform 320ms cubic-bezier(0.22,1,0.36,1),
                      border-color 320ms ease,
                      background 320ms ease,
                      box-shadow 320ms ease;
        }
        .about-milestone-card:hover,
        .about-milestone.pinned .about-milestone-card {
          transform: translateY(-3px);
          border-color: ${LIME};
          background: rgba(159,175,8,0.04);
          box-shadow: 0 18px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(159,175,8,0.18);
        }
        .about-milestone-left  .about-milestone-card { grid-column: 1; }
        .about-milestone-left  .about-milestone-branch { grid-column: 1 / -1; }
        .about-milestone-right .about-milestone-card { grid-column: 2; }
        .about-milestone-right .about-milestone-branch { grid-column: 1 / -1; }

        .about-milestone-status {
          display: flex; align-items: center; gap: 10px;
          margin-bottom: 14px;
        }
        .about-milestone-dot {
          width: 18px; height: 18px;
          border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .about-milestone-dot.done {
          background: ${LIME};
          color: ${EARTH_DEEP};
          box-shadow: 0 0 0 4px rgba(159,175,8,0.18);
        }
        .about-milestone-dot.progress {
          background: ${LIME};
          box-shadow: 0 0 0 4px rgba(159,175,8,0.18), 0 0 12px rgba(159,175,8,0.55);
          animation: about-ms-progress 1.6s ease-in-out infinite;
        }
        @keyframes about-ms-progress {
          0%, 100% { transform: scale(1);   box-shadow: 0 0 0 4px rgba(159,175,8,0.18), 0 0 12px rgba(159,175,8,0.55); }
          50%      { transform: scale(1.18); box-shadow: 0 0 0 8px rgba(159,175,8,0.10), 0 0 18px rgba(159,175,8,0.7);  }
        }
        .about-milestone-dot.upcoming {
          border: 1.5px solid ${LIME};
          background: transparent;
        }
        .about-milestone-dot.future {
          border: 1.5px dashed ${OLIVE};
          background: transparent;
        }
        .about-milestone-dot.aspirational {
          background: ${LIME};
          box-shadow: 0 0 0 4px rgba(159,175,8,0.20),
                      0 0 24px rgba(159,175,8,0.85),
                      0 0 48px rgba(159,175,8,0.45);
          animation: about-ms-asp 3.4s ease-in-out infinite;
        }
        @keyframes about-ms-asp {
          0%, 100% { transform: scale(1);   }
          50%      { transform: scale(1.18); }
        }
        .about-milestone-statuslbl {
          font-family: ${FONT_BODY}; font-size: 10px;
          letter-spacing: 0.32em; text-transform: uppercase;
          font-weight: 700; color: ${LIME};
        }
        .about-milestone-date {
          font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
          font-size: 11px;
          letter-spacing: 0.18em;
          color: ${CREAM_60};
          margin-bottom: 10px;
        }
        .about-milestone-head {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(1.25rem, 1.8vw, 1.55rem);
          color: ${CREAM}; margin: 0 0 10px;
          letter-spacing: -0.02em; line-height: 1.2;
        }
        .about-milestone-body {
          font-family: ${FONT_BODY}; font-size: 14px;
          line-height: 1.6; color: ${CREAM_60}; margin: 0;
        }
        .about-milestone-highlight {
          margin-top: 14px;
          padding: 10px 14px;
          border-radius: 10px;
          background: rgba(159,175,8,0.08);
          border: 1px solid rgba(159,175,8,0.22);
          font-family: ${FONT_BODY}; font-size: 12px;
          letter-spacing: 0.10em;
          color: ${LIME}; font-weight: 600;
          display: flex; align-items: center; gap: 10px;
        }

        /* Center-line node + branch (decorative) */
        .about-milestone-node {
          position: absolute;
          top: 50%; left: 50%;
          width: 12px; height: 12px;
          border-radius: 50%;
          background: ${EARTH_DEEP};
          border: 2px solid ${LIME};
          transform: translate(-50%, -50%);
          z-index: 2;
          box-shadow: 0 0 12px rgba(159,175,8,0.55);
        }
        .about-milestone-branch {
          position: absolute;
          top: 50%;
          height: 1px;
          background: ${CREAM_18};
          z-index: 1;
        }
        .about-milestone-left .about-milestone-branch  { left: 50%; right: 0; }
        .about-milestone-right .about-milestone-branch { left: 0;   right: 50%; }

        @media (max-width: 767px) {
          .about-milestone {
            grid-template-columns: 1fr !important;
            padding-left: 44px;
            margin: 24px 0;
          }
          .about-milestone-card { grid-column: 1 !important; }
          .about-milestone-branch { display: none; }
          .about-milestone-node {
            top: 30px;
            left: 18px;
            transform: translate(-50%, 0);
          }
        }

        .about-d-closing {
          margin-top: clamp(64px, 10vh, 120px);
          padding-top: clamp(40px, 6vh, 64px);
          border-top: 1px solid ${CREAM_10};
          text-align: center;
        }
        .about-d-closing-h {
          font-family: ${FONT_DISP}; font-weight: 500;
          font-size: clamp(2rem, 4vw, 3rem);
          color: ${CREAM};
          letter-spacing: -0.025em; line-height: 1.1;
          margin: 0;
        }
        .about-d-closing-i {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.1rem, 1.6vw, 1.5rem);
          color: ${LIME}; margin: 22px auto 0;
          max-width: 50ch; line-height: 1.4; font-weight: 400;
        }
        .about-d-cta {
          display: flex; flex-wrap: wrap; gap: 18px;
          justify-content: center; margin-top: clamp(40px, 6vh, 64px);
        }
        .about-d-cta a {
          font-family: ${FONT_BODY}; font-size: 13px;
          letter-spacing: 0.22em; text-transform: uppercase;
          font-weight: 600; color: ${CREAM};
          padding: 14px 26px; border-radius: 999px;
          border: 1px solid ${CREAM_18};
          background: rgba(255,255,255,0.03);
          text-decoration: none;
          transition: border-color 280ms ease, background 280ms ease, transform 280ms ease, color 280ms ease;
        }
        .about-d-cta a:hover {
          border-color: ${LIME};
          background: rgba(159,175,8,0.10);
          color: ${LIME};
          transform: translateY(-2px);
        }

        /* ── Reduced motion ──────────────────────────────────────── */
        @media (prefers-reduced-motion: reduce) {
          .about-hero-photo img,
          .about-portrait-photo img { animation: none !important; transition: none !important; }
          .about-sdg-num,
          .about-milestone-dot.progress,
          .about-milestone-dot.aspirational,
          .about-hero-cue-line { animation: none !important; }
        }
      `}</style>

      {/* Mini chapter nav (fixed) */}
      <MiniNav
        items={[
          { id: 'about-a', letter: 'A', label: t('miniNavA') },
          { id: 'about-b', letter: 'B', label: t('miniNavB') },
          { id: 'about-c', letter: 'C', label: t('miniNavC') },
          { id: 'about-d', letter: 'D', label: t('miniNavD') },
        ]}
        activeId={activeChapter}
      />

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        id="about-hero"
        style={{
          position: 'relative',
          background: EARTH_DEEP,
          paddingTop:    'clamp(160px, 18vh, 220px)',
          paddingBottom: 'clamp(160px, 18vh, 220px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <div className="about-grain" />

        {/* Hero grid */}
        <div className="about-content about-hero-grid" style={{ position: 'relative', zIndex: 4 }}>
          <div>
            <p data-hero-anim style={{
              fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.42em',
              textTransform: 'uppercase', fontWeight: 600, color: OLIVE,
              margin: '0 0 28px',
            }}>{t('heroEyebrow')}</p>

            <h1 ref={heroHeadlineRef} className="about-hero-headline">
              <span data-hero-word style={{ display: 'inline-block' }}>{t('heroLine1')}</span>
              <br />
              <span data-hero-word style={{ display: 'inline-block' }}>
                {t('heroLine2Pre')}<em>{t('heroLine2Em')}</em>{t('heroLine2Post')}
              </span>
            </h1>

            <p data-hero-anim className="about-hero-sub">{t('heroSubhead')}</p>

            <div data-hero-anim className="about-hero-jumps">
              <button type="button" className="about-hero-jump" onClick={() => scrollTo('about-a')}>{t('heroJumpA')}</button>
              <button type="button" className="about-hero-jump" onClick={() => scrollTo('about-b')}>{t('heroJumpB')}</button>
              <button type="button" className="about-hero-jump" onClick={() => scrollTo('about-c')}>{t('heroJumpC')}</button>
              <button type="button" className="about-hero-jump" onClick={() => scrollTo('about-d')}>{t('heroJumpD')}</button>
            </div>
          </div>

          <div ref={heroPhotoRef} className="about-hero-photo">
            <Image
              src={PHOTOS.heroCeremony}
              alt={t('heroPhotoAlt')}
              fill
              priority
              sizes="(max-width: 1023px) 100vw, 60vw"
              style={{ objectFit: 'cover', objectPosition: 'center 30%' }}
            />
            <span className="about-hero-photo-cap">{t('heroPhotoCaption')}</span>
          </div>
        </div>

        <div className="about-hero-bottom">
          <div data-hero-anim className="about-hero-bottom-cue">
            <div className="about-hero-cue">
              <span>{t('scrollCue')}</span>
              <span aria-hidden="true" className="about-hero-cue-line" />
            </div>
          </div>
          <span data-hero-anim className="about-hero-bottom-page">{t('pageIndicator')}</span>
        </div>
      </section>

      {/* ── A · ORIGIN ────────────────────────────────────────────── */}
      <section
        id="about-a"
        style={{
          position: 'relative',
          background: `linear-gradient(180deg, ${EARTH_DEEP} 0%, ${EARTH_WARM_1} 100%)`,
          paddingTop:    'clamp(96px, 14vh, 160px)',
          paddingBottom: 'clamp(96px, 14vh, 160px)',
          overflow: 'hidden',
        }}
      >
        <div className="about-grain" />
        <div className="about-content">
          <SectionHeader letter="A" chrome={t('aChrome')} title={t('aTitle')} subtitle={t('aSubtitle')} />

          <p className="about-pullquote">
            {t('aQuotePart1')}<em>{t('aQuoteEm1')}</em>{t('aQuotePart2')}<em>{t('aQuoteEm2')}</em>{t('aQuotePart3')}
          </p>
          <p className="about-pullquote-body">{t('aBody')}</p>

          {/* Foundational milestone split */}
          <div className="about-a-split">
            <div>
              <div style={{
                fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.42em',
                textTransform: 'uppercase', fontWeight: 600, color: LIME,
                marginBottom: 22,
              }}>{t('aMilestoneLabel')}</div>
              <h3 style={{
                fontFamily: FONT_DISP, fontWeight: 500,
                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                color: CREAM, lineHeight: 1.1,
                letterSpacing: '-0.02em', margin: '0 0 22px',
              }}>{t('aMilestoneTitle')}</h3>
              <p style={{
                fontFamily: FONT_BODY, fontSize: 'clamp(15px, 1.05vw, 17px)',
                lineHeight: 1.7, color: CREAM_80, margin: '0 0 24px', maxWidth: '38ch',
              }}>{t('aMilestoneBody')}</p>
              <div>
                <span className="about-tag">{t('aTag1')}</span>
                <span className="about-tag">{t('aTag2')}</span>
                <span className="about-tag">{t('aTag3')}</span>
              </div>
            </div>
            <figure style={{ margin: 0 }}>
              <div className="about-photo">
                <Image src={PHOTOS.pitching1} alt={t('aPhoto1Alt')} fill sizes="(max-width: 1023px) 100vw, 50vw" style={{ objectFit: 'cover' }} />
              </div>
              <figcaption className="about-photo-cap">{t('aPhoto1Caption')}</figcaption>
            </figure>
          </div>

          {/* Photo strip */}
          <div className="about-a-strip">
            <figure style={{ margin: 0 }}>
              <div className="about-photo">
                <Image src={PHOTOS.pitching2} alt={t('aPhoto2Alt')} fill sizes="(max-width: 767px) 100vw, 33vw" style={{ objectFit: 'cover' }} />
              </div>
              <figcaption className="about-photo-cap">{t('aPhoto2Caption')}</figcaption>
            </figure>
            <figure style={{ margin: 0 }}>
              <div className="about-photo">
                <Image src={PHOTOS.claraStage} alt={t('aPhoto3Alt')} fill sizes="(max-width: 767px) 100vw, 33vw" style={{ objectFit: 'cover' }} />
              </div>
              <figcaption className="about-photo-cap">{t('aPhoto3Caption')}</figcaption>
            </figure>
            <figure style={{ margin: 0 }}>
              <div className="about-photo">
                <Image src={PHOTOS.workspace} alt={t('aPhoto4Alt')} fill sizes="(max-width: 767px) 100vw, 33vw" style={{ objectFit: 'cover' }} />
              </div>
              <figcaption className="about-photo-cap">{t('aPhoto4Caption')}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ── B · MISSION & VISION ─────────────────────────────────── */}
      <section
        id="about-b"
        style={{
          position: 'relative',
          background: `linear-gradient(180deg, ${EARTH_WARM_1} 0%, ${EARTH_WARM_2} 100%)`,
          paddingTop:    'clamp(96px, 14vh, 160px)',
          paddingBottom: 'clamp(96px, 14vh, 160px)',
          overflow: 'hidden',
        }}
      >
        <div className="about-grain" />
        <div className="about-content">
          <SectionHeader letter="B" chrome={t('bChrome')} title={t('bTitle')} subtitle={t('bSubtitle')} />

          {/* Mission */}
          <div className="about-statement">
            <div className="about-statement-label">{t('missionLabel')}</div>
            <h3 className="about-statement-h">
              <span>{t('missionLine1')}</span>
              <span>{t('missionLine2')}</span>
              <span>{t('missionLine3')}</span>
            </h3>
            <p className="about-statement-sub">
              {t('missionItalicPart1')}<em>{t('missionItalicEm1')}</em>{t('missionItalicPart2')}<em>{t('missionItalicEm2')}</em>{t('missionItalicPart3')}
            </p>
          </div>

          {/* Vision */}
          <div className="about-statement">
            <div className="about-statement-label">{t('visionLabel')}</div>
            <h3 className="about-statement-h">
              <span>{t('visionLine1')}</span>
              <span>{t('visionLine2')}</span>
              <span>{t('visionLine3')}</span>
            </h3>
            <p className="about-statement-sub">{t('visionItalic')}</p>
          </div>

          {/* SDG cards */}
          <div className="about-sdg-section">
            <h3 className="about-sdg-h">{t('sdgHeader')}</h3>
            <div className="about-sdg-grid">
              <article className="about-sdg-card">
                <div className="about-sdg-num">02</div>
                <h4 className="about-sdg-title">{t('sdg2Title')}</h4>
                <p className="about-sdg-body">{t('sdg2Body')}</p>
              </article>
              <article className="about-sdg-card">
                <div className="about-sdg-num">13</div>
                <h4 className="about-sdg-title">{t('sdg13Title')}</h4>
                <p className="about-sdg-body">{t('sdg13Body')}</p>
              </article>
              <article className="about-sdg-card">
                <div className="about-sdg-num">15</div>
                <h4 className="about-sdg-title">{t('sdg15Title')}</h4>
                <p className="about-sdg-body">{t('sdg15Body')}</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* ── C · TEAM PORTRAITS ───────────────────────────────────── */}
      <section
        id="about-c"
        style={{
          position: 'relative',
          background: EARTH_DEEP,
          paddingTop:    'clamp(96px, 14vh, 160px)',
          paddingBottom: 'clamp(96px, 14vh, 160px)',
          overflow: 'hidden',
        }}
      >
        <div className="about-grain" />
        <div className="about-content">
          <SectionHeader letter="C" chrome={t('cChrome')} title={t('cTitle')} subtitle={t('cSubtitle')} />
          <p className="about-c-tagline">{t('cTagline')}</p>

          {(teamMembers && teamMembers.length > 0
            ? teamMembers
            : ([
                { label: t('hugoLabel'), firstName: t('hugoFirstName'), lastName: t('hugoLastName'), role: t('hugoRole'), body: t('hugoBody'), tags: [t('hugoTag1'), t('hugoTag2'), t('hugoTag3')], photoSrc: PHOTOS.hugo, photoAlt: t('hugoPhotoAlt') },
                { label: t('claraLabel'), firstName: t('claraFirstName'), lastName: t('claraLastName'), role: t('claraRole'), body: t('claraBody'), tags: [t('claraTag1'), t('claraTag2'), t('claraTag3')], photoSrc: PHOTOS.clara, photoAlt: t('claraPhotoAlt') },
                { label: t('alexLabel'), firstName: t('alexFirstName'), lastName: t('alexLastName'), role: t('alexRole'), body: t('alexBody'), tags: [t('alexTag1'), t('alexTag2'), t('alexTag3')], photoSrc: PHOTOS.alex, photoAlt: t('alexPhotoAlt') },
              ] as TeamMember[])
          ).map((m, i) => (
            <Portrait
              key={m.id ?? i}
              side={i % 2 === 0 ? 'right' : 'left'}
              label={m.label}
              firstName={m.firstName}
              lastName={m.lastName}
              role={m.role}
              body={m.body}
              tags={m.tags}
              photoSrc={m.photoSrc}
              photoAlt={m.photoAlt}
            />
          ))}

          <div className="about-c-closing">
            <h3 className="about-c-closing-h">
              <em>{t('cClosingHead')}</em>
            </h3>
            <p className="about-c-closing-body">{t('cClosingBody')}</p>
          </div>
        </div>
      </section>

      {/* ── D · ROADMAP ──────────────────────────────────────────── */}
      <section
        id="about-d"
        style={{
          position: 'relative',
          background: `linear-gradient(180deg, ${EARTH_DEEP} 0%, ${EARTH_FWD} 100%)`,
          paddingTop:    'clamp(96px, 14vh, 160px)',
          paddingBottom: 'clamp(120px, 16vh, 200px)',
          overflow: 'hidden',
        }}
      >
        <div className="about-grain" />
        <div className="about-content">
          <SectionHeader letter="D" chrome={t('dChrome')} title={t('dTitle')} subtitle={t('dSubtitle')} />
          <p className="about-d-intro">{t('dIntro')}</p>

          <div ref={timelineRef} className="about-timeline">
            <span className="about-timeline-axis" aria-hidden="true" />
            <span ref={timelineLineRef} className="about-timeline-fill" aria-hidden="true" />

            {/* Year markers — positioned proportionally down the rail */}
            <div className="about-timeline-years" aria-hidden="true">
              {yearMarkers.map((y, i) => (
                <span
                  key={y}
                  className="about-timeline-year"
                  style={{ top: `${(i / (yearMarkers.length - 1)) * 100}%` }}
                >
                  {y}
                </span>
              ))}
            </div>

            {(milestones && milestones.length > 0
              ? milestones
              : ([
                  { date: t('m1Date'), status: 'done',         statusLabel: t('m1Status'), head: t('m1Head'), body: t('m1Body') },
                  { date: t('m2Date'), status: 'done',         statusLabel: t('m2Status'), head: t('m2Head'), body: t('m2Body') },
                  { date: t('m3Date'), status: 'done',         statusLabel: t('m3Status'), head: t('m3Head'), body: t('m3Body') },
                  { date: t('m4Date'), status: 'progress',     statusLabel: t('m4Status'), head: t('m4Head'), body: t('m4Body') },
                  { date: t('m5Date'), status: 'upcoming',     statusLabel: t('m5Status'), head: t('m5Head'), body: t('m5Body') },
                  { date: t('m6Date'), status: 'upcoming',     statusLabel: t('m6Status'), head: t('m6Head'), body: t('m6Body') },
                  { date: t('m7Date'), status: 'future',       statusLabel: t('m7Status'), head: t('m7Head'), body: t('m7Body'), highlight: t('m7Highlight') },
                  { date: t('m8Date'), status: 'future',       statusLabel: t('m8Status'), head: t('m8Head'), body: t('m8Body'), highlight: t('m8Highlight') },
                  { date: t('m9Date'), status: 'aspirational', statusLabel: t('m9Status'), head: t('m9Head'), body: t('m9Body'), highlight: t('m9Highlight') },
                ] as Milestone[])
            ).map((m, i) => (
              <Milestone
                key={m.id ?? i}
                index={i}
                side={i % 2 === 0 ? 'right' : 'left'}
                date={m.date}
                status={m.status}
                statusLabel={m.statusLabel}
                head={m.head}
                body={m.body}
                highlight={m.highlight}
              />
            ))}
          </div>

          <div className="about-d-closing">
            <h3 className="about-d-closing-h">{t('dClosingHead')}</h3>
            <p className="about-d-closing-i">{t('dClosingItalic')}</p>
            <div className="about-d-cta">
              <a href="/tech">{t('ctaTech')}</a>
              <a href={contactHref}>{t('ctaContact')}</a>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
